import { useEffect, useState } from 'react';

/**
 * The value, but only after it has stopped changing for `delay` milliseconds.
 *
 * Used for anything that turns typing into a request. Filtering in the browser
 * was free per keystroke; asking the server is not, and a burst of requests
 * also means replies arriving out of order.
 *
 * Because the request is keyed on the settled value, a reply for an older
 * search is never the one on screen: the query for it is no longer the active
 * one, so a slow answer cannot overwrite a newer result.
 */
export function useDebounced<T>(value: T, delay = 300): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    // Clearing on every change is what makes it wait for a pause rather than
    // firing once per keystroke on a fixed schedule.
    return () => clearTimeout(timer);
  }, [value, delay]);

  return settled;
}
